// GAVION contact-call: the calls made to one contact (calls.contact_id), on the contact page.
// GAVION granola-calls: each call's Granola notes (meeting_notes.call_id) under it, as on the lead timeline.
import {
  useGetList,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";
import { demoLinked } from "../domain/dialer/contactCall"; // GAVION demo-contact
import { formatPhoneDisplay } from "../domain/phone";
import { formatLeadDateTime } from "../domain/torontoTime";
import { CallOutcomeBadge } from "../leads/badges";
import { CALL_OUTCOMES, type CallOutcome } from "../leads/types";
import { MeetingNoteCard } from "../meetings/MeetingNoteCard";
import type { MeetingNoteRow } from "../meetings/types";

type CallRow = {
  id: Identifier;
  dialed_at: string;
  outcome: string | null;
  to_number: string | null;
  note: string | null;
  demo?: unknown; // GAVION demo-contact: demo.contact_linked
};
const isOutcome = (v: unknown): v is CallOutcome =>
  typeof v === "string" && (CALL_OUTCOMES as readonly string[]).includes(v);

export const ContactCallsList = ({ contactId }: { contactId: Identifier }) => {
  const t = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { data: calls = [], isPending } = useGetList<CallRow>("calls", {
    filter: { contact_id: contactId },
    pagination: { page: 1, perPage: 20 },
    sort: { field: "dialed_at", order: "DESC" },
  });
  const ids = calls.map((c) => c.id);
  const { data: notes = [] } = useGetList<MeetingNoteRow>(
    "meeting_notes",
    {
      filter: { "call_id@in": `(${ids.join(",")})`, match_status: "matched" },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: ids.length > 0 },
  );
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-lg font-semibold">
        {t("gavion.dialer.contact_call.calls_title")}
      </h3>
      {isPending ? null : calls.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("gavion.dialer.contact_call.no_calls")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2 text-sm">
          {calls.map((c) => (
            <li key={c.id} className="flex flex-col gap-0.5">
              <div className="flex flex-wrap items-center gap-2">
                {isOutcome(c.outcome) ? (
                  <CallOutcomeBadge outcome={c.outcome} />
                ) : null}
                <time dateTime={c.dialed_at} className="text-muted-foreground">
                  {formatLeadDateTime(
                    c.dialed_at,
                    locale === "en" ? "en" : "fr",
                  )}
                </time>
                {c.to_number ? (
                  <span className="tabular-nums text-muted-foreground">
                    {formatPhoneDisplay(c.to_number)}
                  </span>
                ) : null}
                {/* GAVION demo-contact: his demo was booked on the lead's main line, not a call to his number */}
                {demoLinked(c) ? (
                  <span className="text-muted-foreground">
                    {t("gavion.dialer.contact_call.demo_main_line")}
                  </span>
                ) : null}
              </div>
              {c.note ? <p className="whitespace-pre-line">{c.note}</p> : null}
              {notes
                .filter(
                  (n) =>
                    n.call_id != null && String(n.call_id) === String(c.id),
                )
                .map((n) => (
                  <div key={n.id} className="mt-1 border-l-2 pl-3">
                    <MeetingNoteCard note={n} variant="call" />
                  </div>
                ))}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
